<template>
  <section
    v-if="providers.length"
    class="trust-strip"
    aria-labelledby="trust-strip-label"
  >
    <header class="trust-strip__head">
      <h2
        id="trust-strip-label"
        class="trust-strip__heading text-head-2 text-sora text-bold mobile-font--h4"
      >
        {{ $t("main.m_trust_sourced_from") }}
      </h2>
    </header>
    <div class="trust-strip__marquee">
      <ul class="trust-strip__track" aria-label="Providers">
        <li
          v-for="(provider, idx) in marqueeProviders"
          :key="`${provider.id}-${idx}`"
          class="trust-strip__item"
          :title="provider.name"
          :aria-hidden="idx >= providers.length ? 'true' : undefined"
        >
          <img
            v-if="provider.logo"
            :src="provider.logo"
            :alt="provider.name"
            class="trust-strip__logo"
            loading="lazy"
            width="120"
            height="40"
          >
          <span v-else class="trust-strip__name text-body-2">
            {{ provider.name }}
          </span>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useAdminStore } from "~/store/admin";

interface ProviderLogo {
  id: string;
  name: string;
  logo?: string;
}

const adminStore = useAdminStore();
const providers = ref<ProviderLogo[]>([]);

const marqueeProviders = computed<ProviderLogo[]>(() => [
  ...providers.value,
  ...providers.value,
]);

onMounted(async () => {
  try {
    const data = await adminStore.getProviders();
    if (!Array.isArray(data)) return;
    providers.value = data
      .filter((p) => p?.name)
      .map((p) => ({
        id: p.id,
        name: p.name,
        logo: p.logoFile?.url,
      }));
  } catch (e) {
    console.warn("[TrustStrip] failed to load providers", e);
  }
});
</script>

<style scoped lang="scss">
.trust-strip {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__heading {
    margin: 0;
  }

  &__marquee {
    width: 100%;
    overflow: hidden;
    mask-image: linear-gradient(
      to right,
      transparent 0,
      black 6%,
      black 94%,
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0,
      black 6%,
      black 94%,
      transparent 100%
    );

    &:hover .trust-strip__track,
    &:focus-within .trust-strip__track {
      animation-play-state: paused;
    }
  }

  &__track {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 48px;
    width: max-content;
    animation: trust-strip-scroll 40s linear infinite;
    will-change: transform;
  }

  &__item {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
  }

  &__logo {
    max-height: 32px;
    max-width: 120px;
    width: auto;
    height: auto;
    object-fit: contain;
  }

  &__name {
    color: var(--black-monochrome-60);
    font-weight: 600;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }
}

@keyframes trust-strip-scroll {
  from {
    transform: translate3d(0, 0, 0);
  }
  to {
    transform: translate3d(-50%, 0, 0);
  }
}

@media (max-width: 48rem) {
  .trust-strip {
    padding: 16px 0;
    &__track {
      gap: 28px;
      animation-duration: 30s;
    }
    &__logo {
      max-height: 24px;
      max-width: 88px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .trust-strip__track {
    animation: none;
    transform: none;
  }
}
</style>
